import React, { useContext, useEffect, useState, useRef } from 'react';
import { graphql, PageRendererProps } from 'gatsby';
import querystring from 'query-string';

import { LocaleContext } from '@repo/contexts/LocaleContext';
import getProductSchema from '@repo/utils/getProductSchema';
import BreadcrumbWithWrapper from '@repo/ui-components/BreadcrumbWithWrapper';
import { GetNonTransactionalProductPageQuery } from '@repo/models/gatsby-graphql-types';

import Layout from '@components/layout';
import getInternalLink from '@components/utils/getInternalLink';
import BuyBox from '@components/lead-generation/BuyBox';
import SectionNav from '@components/lead-generation/SectionNav';
import MarketingContentSections from '@components/lead-generation/MarketingContentSections';
import BlobNav from '@components/lead-generation/BlobNav';
import CtaNav from '@components/lead-generation/CtaNav';

import generateHeadTagData from '../utils/generateHeadTagData';

const getBreadcrumbs = (currency, collection, sanityProduct) => {
  const breadcrumbs = [
    { link: getInternalLink({ slug: '/', currency }), text: 'Home' },
  ];

  if (collection?.parentCollection?.store?.title) {
    breadcrumbs.push({
      text: collection.parentCollection.store.title,
      link: getInternalLink({
        slug: `/${collection.parentCollection.store.slug.current}`,
        currency,
      }),
    });
    breadcrumbs.push({
      text: collection.store.title,
      link: getInternalLink({
        slug: `/${collection.parentCollection.store.slug.current}/${collection.store.slug.current}`,
        currency,
      }),
    });
  } else if (collection) {
    breadcrumbs.push({
      text: collection.store.title,
      link: getInternalLink({
        slug: `/${collection.store.slug.current}`,
        currency,
      }),
    });
  }

  breadcrumbs.push({ text: sanityProduct.product?.title, link: null });

  return breadcrumbs;
};

interface LeadGenPDPProps extends PageRendererProps {
  data: GetNonTransactionalProductPageQuery;
}

function LeadGenPDP({ data, location }: Readonly<LeadGenPDPProps>) {
  const { currency } = useContext(LocaleContext);
  const query = querystring.parse(location.search);

  const { sanityProduct } = data;
  const {
    SEOMetadata,
    store,
    description,
    collection,
    marketingContentSections,
    nonTransactionalCTA,
    desktopBackground,
    mobileBackground,
  } = sanityProduct;

  const [productSchemaData, setProductSchemaData] = useState(null);
  const [activeSku] = useState(query?.sku ?? null);
  const [activeSlide, setActiveSlide] = useState(0);
  const [blobNavActive, setBlobNavActive] = useState(false);

  const startSectionRefs = useRef([]);
  const buttonRefs = useRef([]);
  const navRef = useRef(null);
  const buyBoxRef = useRef(null);
  const isProgrammaticScrolling = useRef(null);

  const selectedVariant =
    sanityProduct.product?.variants.find(
      variant => variant.sku === activeSku
    ) || sanityProduct.product?.variants[0];

  const scrollToSlide = (index, scrollInto = false) => {
    const navElement = navRef.current;
    const buttonElement = buttonRefs.current[index];

    if (navElement && buttonElement) {
      const navWidth = navElement.offsetWidth;
      const buttonWidth = buttonElement.offsetWidth;
      const buttonLeft = buttonElement.offsetLeft;

      // Calculate the scroll position to center the active button
      const scrollPosition = buttonLeft - navWidth / 2 + buttonWidth / 2;

      navElement.scrollTo({
        left: scrollPosition,
        behavior: 'smooth',
      });
    }

    if (scrollInto) {
      const section = startSectionRefs.current[index];
      if (section) {
        const fixedHeaderHeight = 32; // Notification Banner
        const stickyMenuHeight = 80; // Sticky Nav
        const totalOffset = fixedHeaderHeight + stickyMenuHeight;

        const rect = section.getBoundingClientRect();
        const elementTop = rect.top + window.scrollY;
        const targetPosition = elementTop - totalOffset;

        window.scrollTo({
          top: targetPosition,
          behavior: 'smooth',
        });
      }
    }

    setActiveSlide(index);
  };

  const handleSectionClick = index => {
    isProgrammaticScrolling.current = true;
    scrollToSlide(index, true);

    setTimeout(() => {
      isProgrammaticScrolling.current = false;
    }, 1000);
  };

  const setNavRef = (el, index) => {
    buttonRefs.current[index] = el;
  };

  const setStartSectionRef = (el, index) => {
    startSectionRefs.current[index] = el;
  };

  // Watch active sku parameter and update product schema data if sku changes
  useEffect(() => {
    setProductSchemaData(
      JSON.stringify(getProductSchema(sanityProduct?.product, activeSku))
    );
  }, [activeSku, sanityProduct?.product]);

  useEffect(() => {
    let lastScrollY = window.scrollY;

    const startObserver = new IntersectionObserver(
      entries => {
        if (isProgrammaticScrolling.current) return;

        entries.forEach(entry => {
          const index = startSectionRefs.current.indexOf(entry.target);
          if (index === -1) return;

          const scrollingDown = window.scrollY > lastScrollY;
          lastScrollY = window.scrollY;

          if (entry.isIntersecting) {
            // When scrolling down, make the current slide active
            if (scrollingDown) {
              scrollToSlide(index);
            }
          } else if (!scrollingDown) {
            // When scrolling up, make the previous slide active as the current slide leaves the viewport
            scrollToSlide(Math.max(0, index - 1));
          }
        });
      },
      {
        root: null,
        threshold: 0,
      }
    );

    const buyBoxObserver = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          const heightOffset = 80 + 32 + 15;
          if (entry.intersectionRect.height >= heightOffset) {
            setBlobNavActive(false);
          } else {
            setBlobNavActive(true);
          }
        });
      },
      { threshold: [0, 0.01, 0.1, 0.2, 0.5, 1] }
    );

    // Copy ref values to local variables for cleanup
    const startRefs = startSectionRefs.current;

    const currentBuyBoxRef = buyBoxRef.current;

    // Observe the buyBoxRef
    if (buyBoxRef.current) {
      buyBoxObserver.observe(buyBoxRef.current);
    }

    // Observe each section
    startRefs.forEach(section => {
      if (section) startObserver.observe(section);
    });

    return () => {
      // Cleanup observers
      if (currentBuyBoxRef) {
        buyBoxObserver.unobserve(currentBuyBoxRef);
      }
      startRefs.forEach(section => {
        if (section) startObserver.unobserve(section);
      });
    };
  }, []);

  return (
    <Layout
      location={location}
      headTagData={generateHeadTagData(
        SEOMetadata,
        sanityProduct.product,
        description,
        getProductSchema(sanityProduct?.product, null, activeSku),
        selectedVariant,
        location,
        store
      )}
      scriptsData={[
        <script type="application/ld+json" key="pdp-schema">
          {productSchemaData}
        </script>,
      ]}
      fullWidth
    >
      <div className="max-w-screen-xl mx-auto">
        <BreadcrumbWithWrapper
          elements={getBreadcrumbs(currency, collection, sanityProduct)}
        />
      </div>
      {/* Buy Box */}
      <BuyBox
        buyBoxRef={buyBoxRef}
        desktopBackground={desktopBackground}
        mobileBackground={mobileBackground}
        sanityProduct={sanityProduct}
        getInternalLink={getInternalLink}
      />
      {/* Section Nav */}
      {marketingContentSections && (
        <SectionNav
          blobNavActive={blobNavActive}
          navRef={navRef}
          marketingContentSections={marketingContentSections}
          activeSlide={activeSlide}
          handleSectionClick={handleSectionClick}
          setNavRef={setNavRef}
          nonTransactionalCTA={nonTransactionalCTA}
        />
      )}
      {/* Marketing Content Sections */}
      <MarketingContentSections
        marketingContentSections={marketingContentSections}
        setStartSectionRef={setStartSectionRef}
        getInternalLink={getInternalLink}
      />

      {/* Blob Nav */}
      {marketingContentSections && blobNavActive && (
        <BlobNav
          marketingContentSections={marketingContentSections}
          handleSectionClick={handleSectionClick}
          activeSlide={activeSlide}
        />
      )}

      {/* CTA Nav */}
      <CtaNav
        blobNavActive={blobNavActive}
        nonTransactionalCTA={nonTransactionalCTA}
      />
    </Layout>
  );
}

export const query = graphql`
  query GetNonTransactionalProductPage($handle: String = "") {
    sanityProduct(
      store: { slug: { current: { eq: $handle } } }
      siteIdentifier: { eq: "ifb" }
    ) {
      description
      marketingContentSections: _rawMarketingContentSections(
        resolveReferences: { maxDepth: 10 }
      )
      SEOMetadata {
        title
        description
        canonicalUrl
        noIndex
        noFollow
        ogTitle
        ogDescription
        ogUrl
        ogImage {
          asset {
            url
          }
        }
      }
      store: _rawStore(resolveReferences: { maxDepth: 3 })

      product: shopifyProduct {
        shopifyId
        title
        variants {
          title
          requiresShipping
          shopifyId
          availableForSale
          sku
        }
      }
      collection {
        store {
          title
          slug {
            current
          }
        }
        parentCollection {
          store {
            title
            slug {
              current
            }
          }
        }
      }
      desktopBackground {
        asset: _rawAsset(resolveReferences: { maxDepth: 3 })
        alt
      }
      mobileBackground {
        asset: _rawAsset(resolveReferences: { maxDepth: 3 })
        alt
      }
      additionalProductInformation: _rawAdditionalProductInformation(
        resolveReferences: { maxDepth: 3 }
      )
      nonTransactionalCTA {
        _key
        _type
        caption
        blank
        href
        internalLink: _rawInternalLink(resolveReferences: { maxDepth: 3 })
        linkType
      }
    }
  }
`;

export default LeadGenPDP;
